import React, { useEffect, useRef, useState } from 'react';
import { clientOnly } from './clientOnly';
import type Autosuggest from 'react-autosuggest';

const ReactAutosuggest = clientOnly(() => import('react-autosuggest'));

// Callers pass a CSS module as the theme and type onChange with input events.
type Props = Omit<
    Autosuggest.AutosuggestProps<any, any>,
    'theme' | 'inputProps'
> & {
    theme?: Record<string, string | undefined>;
    inputProps: Omit<Autosuggest.InputProps<any>, 'onChange'> & {
        onChange: (event: any, params: Autosuggest.ChangeEvent) => void;
    };
};

const ITEMS_ID = 'react-autowhatever-1';

/**
 * react-autosuggest, loaded only in the browser the first time someone
 * hovers, touches or focuses the input. Until then a plain input with the
 * same markup and theme classes stands in, so the field is in the server
 * HTML, looks the same and accepts typing.
 */
const LazyAutoSuggest: React.FC<Props> = (props) => {
    const [ready, setReady] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const placeholderRef = useRef<HTMLInputElement>(null);
    const refocus = useRef(false);
    const loading = useRef(false);

    const load = () => {
        if (loading.current) return;
        loading.current = true;
        ReactAutosuggest.load().then(() => {
            // Decide at swap time: the user may have focused or left the
            // field while the chunk was loading.
            refocus.current = document.activeElement === placeholderRef.current;
            setReady(true);
        });
    };

    // Hand focus over to the real input once it mounts, caret at the end.
    useEffect(() => {
        if (!ready || !refocus.current) return;
        const input = wrapperRef.current?.querySelector('input');
        if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }
        refocus.current = false;
    }, [ready]);

    const { theme = {}, inputProps } = props;
    // onBlur belongs to the real input, not the placeholder.
    const { onChange, onBlur: _onBlur, ...restInputProps } = inputProps;

    return (
        <div ref={wrapperRef} style={{ display: 'contents' }}>
            {ready ? (
                <ReactAutosuggest {...(props as any)} />
            ) : (
                <div
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-owns={ITEMS_ID}
                    aria-expanded={false}
                    className={theme.container}
                >
                    <input
                        ref={placeholderRef}
                        type="text"
                        autoComplete="off"
                        aria-autocomplete="list"
                        aria-controls={ITEMS_ID}
                        className={theme.input}
                        {...(restInputProps as React.InputHTMLAttributes<HTMLInputElement>)}
                        onChange={(e) =>
                            onChange(e, {
                                newValue: e.target.value,
                                method: 'type',
                            })
                        }
                        onPointerEnter={load}
                        onTouchStart={load}
                        onFocus={load}
                    />
                    <div
                        id={ITEMS_ID}
                        role="listbox"
                        className={theme.suggestionsContainer}
                    />
                </div>
            )}
        </div>
    );
};

export default LazyAutoSuggest;
